import React, { useState, useCallback, useMemo, useRef } from "react";
import { PlusIcon, TrashIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { useOrderTrackStore, TakaEntry, OrderDetail } from "../store/OrderStore"; 
import TakaSticker from "../component/Sticker"; 
import * as htmlToImage from 'html-to-image';
import toast from "react-hot-toast";

interface FormErrors {
    meters?: string;
}

interface WarehouseCardProps {
    title: string;
    subtitle: string;
    maxEntries?: number;
}

const WarehouseCard: React.FC<WarehouseCardProps> = ({ title, subtitle, maxEntries = 100 }) => {
    const warehouseKey = title.toLowerCase().includes('mumbai') ? 'mumbai' : 'surat';
    

    const { trackingData, lotNumber, designId, orderDetails, isDataLoaded, addTaka, removeTaka } = useOrderTrackStore();
    const allTakas = trackingData.takaArrival;
    
    const [meters, setMeters] = useState<string>("");
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const stickerRef = useRef<HTMLDivElement>(null);
    const [stickerData, setStickerData] = useState<any>(null);

    const takas = useMemo(() => allTakas.filter((taka) => taka.warehouse === warehouseKey), [allTakas, warehouseKey]);

    const validateForm = useCallback((): boolean => {
        const newErrors: FormErrors = {};
        if (!meters.trim() || Number(meters) <= 0) {
            newErrors.meters = "Meters must be a positive number.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [meters]);

    const handleAddTaka = async () => {
        if (!validateForm() || isSubmitting) return;
        setIsSubmitting(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 400)); 
            addTaka(warehouseKey, Number(meters)); 
            setMeters("");
            setErrors({});
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 2000);
        } catch (err) {
            console.error("Failed to add taka:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveTaka = (takaId: string) => {
        removeTaka(takaId);
    };

    const getTotalMeters = () => takas.reduce((sum, taka) => sum + taka.meters, 0);

    const handleDownload = async (taka: TakaEntry) => {
        try {
            if (!isDataLoaded) {
                toast.error("Order data is not fully loaded yet.");
                return;
            }

            const dataForSticker = {
                fabricId: designId?.basicInfo?.designNo || "N/A",
                cn: lotNumber?.slice(-4) || "N/A",
                date: new Date(taka.createdAt).toLocaleDateString("en-GB"),
                designNo: designId?.basicInfo?.designNo || "N/A",
                meters: taka.meters.toString(),
                totalTaka: allTakas.length.toString(),
                size: orderDetails
                        ?.map((d: OrderDetail) => d.sizeSegmentId.segmentName)
                        .join(" / ") || "N/A",
                pattern: designId?.basicInfo?.designCategory || "N/A",
            };

            setStickerData(dataForSticker);
            await new Promise((res) => setTimeout(res, 150));
            const stickerElement = stickerRef.current;
            if (!stickerElement) {
                toast.error("Sticker element not found!");
                setStickerData(null);
                return;
            }

            const toastId = toast.loading("Generating sticker...");
            const dataUrl = await htmlToImage.toPng(stickerElement, { quality: 1.0, pixelRatio: 3 });
            const link = document.createElement("a");
            const safeDesignNo = (dataForSticker.designNo || "N_A").replace(/[^\w-]/g, "_");
            const safeCn = (dataForSticker.cn || "N_A").replace(/[^\w-]/g, "_");
            link.download = `sticker-${safeDesignNo}-${safeCn}.png`;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Sticker downloaded successfully!", { id: toastId });
        } catch (error) {
            console.error("❌ Sticker generation failed:", error);
            toast.error("Failed to generate sticker.");
        } finally {
            setStickerData(null);
        }
    };

  return (
    <div className="bg-white border border-gray-200/60 rounded-xl hover:shadow-xl transition-all duration-300 p-8 w-full max-w-4xl mx-auto">
      {/* Header Section */}
      <div className="flex justify-between items-start mb-8">
        <div className="space-y-1">
          <h3 className="text-2xl font-bold text-gray-900 tracking-tight">
            {title}
          </h3>
          <p className="text-gray-600 font-medium">{subtitle}</p>
          {takas.length > 0 && (
            <div className="flex items-center space-x-4 mt-3">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {takas.length} {takas.length === 1 ? "Entry" : "Entries"}
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                {getTotalMeters().toLocaleString()} m Total
              </span>
            </div>
          )}
        </div>
        {showSuccess && (
          <div className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded-lg animate-fade-in">
            <CheckCircleIcon className="h-5 w-5" />
            <span>Taka added!</span>
          </div>
        )}
      </div>

      {/* Form Section (Now simplified) */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-xl p-6 mb-8 border border-gray-100">
        <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
          <PlusIcon className="h-5 w-5 mr-2 text-indigo-600" />
          Add New Taka
        </h4>
        <div className="flex w-full flex-col items-stretch gap-4 sm:flex-row sm:items-end">
          {/* Input Field Group */}
          <div className="w-full flex-grow space-y-2">
            <label className="block text-sm font-semibold text-gray-700">
              Meters <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              placeholder="Enter meters"
              value={meters}
              onChange={(e) => {
                setMeters(e.target.value);
                if (errors.meters)
                  setErrors((p) => ({ ...p, meters: undefined }));
              }}
              className={`w-full px-4 py-3 border-2 rounded-xl text-sm transition-all duration-200 focus:outline-none focus:ring-4 ${
                errors.meters
                  ? "border-red-300 focus:border-red-500 focus:ring-red-100"
                  : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-100 hover:border-gray-300"
              }`}
              disabled={isSubmitting}
            />
            {errors.meters && (
              <div className="flex items-center space-x-1 text-red-600">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="text-xs font-medium">{errors.meters}</span>
              </div>
            )}
          </div>

          {/* Add Button */}
          <button
            onClick={handleAddTaka}
            disabled={isSubmitting || takas.length >= maxEntries}
            className={`flex-shrink-0 px-4 py-3 rounded-xl font-semibold text-white transition-all duration-200 flex items-center justify-center gap-2 ${
              isSubmitting || takas.length >= maxEntries
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 active:scale-95 shadow-lg hover:shadow-xl"
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Adding...</span>
              </>
            ) : (
              <>
                <PlusIcon className="h-4 w-4" />
                <span>Add Taka Entry</span>
              </>
            )}
          </button>
        </div>
      </div>

      {stickerData && (
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <TakaSticker ref={stickerRef} {...stickerData} />
        </div>
      )}


      <div className="space-y-4">
        <h4 className="text-lg font-bold text-gray-800">Received Takas</h4>
        {takas.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
            <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2 2v-5m16 0h-2M4 13h2"
                />
              </svg>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 mb-1">
              No takas added yet
            </h3>
            <p className="text-sm text-gray-400">
              Add your first taka entry using the form above
            </p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      #
                    </th>
                    {/* <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">Taka ID</th> */}
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Meters
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Sticker
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-bold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {takas.map((taka, index) => (
                    <tr
                      key={taka.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {index + 1}
                      </td>
 
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-indigo-600">
                          {taka.meters.toLocaleString()} m
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleDownload(taka)}
                          className="inline-flex items-center p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all duration-200"
                          title="Download Sticker PDF"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleRemoveTaka(taka.id)}
                          className="inline-flex items-center p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200"
                          title="Remove this entry"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WarehouseCard;
